/**
 * The one place this app talks to the network.
 *
 * Four jobs, and each of them exists because doing it per call site goes wrong quietly:
 *
 * 1. **`credentials: 'include'`.** The session is a cookie. A `fetch` without this sends no
 *    cookie and the response is a 401 that looks exactly like an expired session.
 * 2. **The CSRF header on mutations.** Read from the non-`httpOnly` half of the cookie pair and
 *    echoed in `x-csrf-token`, which is what the API's signed double-submit check verifies.
 * 3. **The error envelope, parsed.** `{ error: { code, message, details?, requestId? } }` becomes
 *    a typed {@link ApiError}, so a screen branches on `code` rather than on message text.
 * 4. **Responses validated against the shared Zod schemas.** The API and this app compile against
 *    the same definitions; parsing at the boundary is what makes that guarantee hold at runtime
 *    too, and turns a contract drift into one legible error instead of `undefined` three
 *    components deep.
 */
import { errorResponseSchema, type ErrorCode, type ErrorResponse } from '@catalog/shared';
import type { ZodType } from 'zod';

import { webEnv } from '../env.js';

/** What went wrong, at a level a screen can branch on. */
export type ApiErrorKind =
  /** The API answered with a status outside 2xx. `status` and `code` are from the envelope. */
  | 'http'
  /** The request never completed — offline, DNS, a dropped connection, an abort. */
  | 'network'
  /** The API answered 2xx with a body the shared schema does not accept. */
  | 'malformed';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** The HTTP status, or `0` when there was never a response. */
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: ErrorResponse['error']['details'];
  /** Quote this when reporting a 500; it is the key into the server logs. */
  readonly requestId: string | undefined;

  constructor(
    kind: ApiErrorKind,
    status: number,
    code: ErrorCode,
    message: string,
    options: {
      details?: ErrorResponse['error']['details'];
      requestId?: string | undefined;
      cause?: unknown;
    } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.details = options.details;
    this.requestId = options.requestId;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isUnauthenticated(error: unknown): boolean {
  return isApiError(error) && error.status === 401;
}

export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

/**
 * A message worth putting on screen for any failure at all.
 *
 * The API's own `message` is written for a person and is used verbatim — including, critically,
 * the generic sign-in failure, which must not be elaborated on by the client (00-context §10.5).
 * The two non-HTTP kinds get a sentence here because there is no server to have written one.
 */
export function messageFor(error: unknown): string {
  if (isApiError(error)) {
    if (error.kind === 'network') {
      return 'Could not reach the catalog service. Check your connection and try again.';
    }
    if (error.kind === 'malformed') {
      return 'The catalog service returned a response this app could not read.';
    }
    return error.message;
  }

  return 'Something went wrong.';
}

/* -- The 401 hook ------------------------------------------------------------------------- */

type UnauthorizedListener = () => void;

let onUnauthorized: UnauthorizedListener | undefined;

/**
 * Registered once by `AuthProvider`, which clears the cached user and redirects to `/login`.
 *
 * A module-level hook rather than a React context because the thing that needs to fire it is the
 * fetch wrapper, which is called from query functions that have no component to read context
 * from. Returns its own unsubscribe so a remount cannot leave a stale listener behind.
 */
export function setUnauthorizedHandler(listener: UnauthorizedListener): () => void {
  onUnauthorized = listener;

  return () => {
    if (onUnauthorized === listener) onUnauthorized = undefined;
  };
}

/* -- Query strings ----------------------------------------------------------------------- */

export type QueryValue = string | number | boolean | readonly string[] | undefined | null;

/**
 * Builds a query string, repeating a key per array entry.
 *
 * The API accepts both `?level=a&level=b` and `?level=a,b` (see `csvArray` in
 * `@catalog/shared`); the repeated form is used because it needs no escaping decision about
 * values that contain a comma — and plenty of skill names do.
 *
 * `undefined`, `null` and the empty string are dropped rather than sent. An empty `q` is a
 * validation failure on every endpoint that takes one, so "the user cleared the box" must not
 * become `?q=`.
 */
export function toQueryString(params: Readonly<Record<string, QueryValue>>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    /**
     * `typeof value === 'object'`, not `Array.isArray`.
     *
     * `Array.isArray` is typed `(arg: any) => arg is any[]`, so it widens the narrowed union
     * back to `any[]` and every use of the element becomes an unsafe access. After the
     * `undefined`/`null` guard above, an object is the only array-shaped member of `QueryValue`,
     * so the `typeof` check narrows it precisely.
     */
    if (typeof value === 'object') {
      for (const entry of value) {
        if (entry.length > 0) search.append(key, entry);
      }
      continue;
    }

    const encoded = typeof value === 'string' ? value : String(value);
    if (encoded.length > 0) search.append(key, encoded);
  }

  const query = search.toString();

  return query.length > 0 ? `?${query}` : '';
}

/* -- The cookie --------------------------------------------------------------------------- */

/**
 * Reads one cookie by name.
 *
 * `document.cookie` is a single string of `name=value` pairs; the name is matched exactly rather
 * than by `includes`, because `catalog_session_csrf` is a substring of nothing today and would
 * be a substring of `catalog_session_csrf_v2` tomorrow.
 */
export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  for (const pair of document.cookie.split('; ')) {
    const separator = pair.indexOf('=');
    if (separator === -1) continue;
    if (pair.slice(0, separator) !== name) continue;

    return decodeURIComponent(pair.slice(separator + 1));
  }

  return undefined;
}

/* -- The request -------------------------------------------------------------------------- */

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface RequestOptions<Result> {
  /** Validated against this before the value is returned. */
  readonly schema: ZodType<Result>;
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly searchParams?: Readonly<Record<string, QueryValue>>;
  readonly signal?: AbortSignal | undefined;
  /**
   * Suppresses the global 401 hook for this request.
   *
   * Exactly one caller needs it: `GET /auth/me`, whose 401 *is* the answer "nobody is signed
   * in". Letting that fire the hook would redirect every anonymous visitor away from the public
   * catalog the moment the page loaded.
   */
  readonly ignoreUnauthorized?: boolean;
}

/**
 * `path` is relative to `/api/v1`, e.g. `/courses`.
 *
 * Use {@link apiFetchAbsolute} for the two Endeavor X endpoints, which the API registers at the
 * root rather than under the version prefix.
 */
export async function apiFetch<Result>(
  path: string,
  options: RequestOptions<Result>,
): Promise<Result> {
  return request(`${webEnv.apiPrefix}${path}`, options);
}

export async function apiFetchAbsolute<Result>(
  path: string,
  options: RequestOptions<Result>,
): Promise<Result> {
  return request(`${webEnv.apiBaseUrl}${path}`, options);
}

async function request<Result>(url: string, options: RequestOptions<Result>): Promise<Result> {
  const method = options.method ?? 'GET';
  const target = `${url}${toQueryString(options.searchParams ?? {})}`;

  const headers = new Headers({ accept: 'application/json' });
  if (options.body !== undefined) headers.set('content-type', 'application/json');

  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie(webEnv.csrfCookieName);
    if (csrf !== undefined) headers.set('x-csrf-token', csrf);
  }

  let response: Response;
  try {
    response = await fetch(target, {
      method,
      headers,
      // The session is a cookie, and in a cross-origin deployment nothing is sent without this.
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    /**
     * An abort is not a failure to report.
     *
     * TanStack Query aborts in-flight requests when a query key changes — which happens on every
     * keystroke in the search box — and surfacing those as errors would flash an error state
     * over perfectly healthy results. Rethrowing the original lets the library recognise it.
     */
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;

    throw new ApiError('network', 0, 'SERVICE_UNAVAILABLE', 'The request could not be completed', {
      cause,
    });
  }

  if (!response.ok) throw await apiErrorFromResponse(response, options.ignoreUnauthorized ?? false);

  // 204 has no body. Nothing in this API returns one today, and a schema that accepts
  // `undefined` is the caller's business rather than a special case here.
  const payload: unknown = response.status === 204 ? undefined : await readJson(response);

  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      'malformed',
      response.status,
      'INTERNAL_ERROR',
      'The response did not match the expected shape',
      { cause: parsed.error },
    );
  }

  return parsed.data;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return undefined;

  try {
    return JSON.parse(text);
  } catch (cause) {
    throw new ApiError(
      'malformed',
      response.status,
      'INTERNAL_ERROR',
      'The response was not JSON',
      {
        cause,
      },
    );
  }
}

/**
 * Turns a non-2xx response into an {@link ApiError}, and fires the 401 hook on the way past.
 *
 * A body that is not the standard envelope — a proxy's own 502 page, say — still produces an
 * `ApiError` with a status, because the status is the part a screen needs most.
 *
 * Exported for the one caller that cannot go through {@link apiFetch}: the agentic search stream
 * reads a `ReadableStream` rather than a JSON body, so it does its own `fetch` — and it still has
 * to produce the same typed error and still has to fire the 401 hook, or a session that expires
 * mid-search leaves the page showing a streaming failure instead of signing the user out.
 */
export async function apiErrorFromResponse(
  response: Response,
  ignoreUnauthorized = false,
): Promise<ApiError> {
  let envelope: ErrorResponse | undefined;
  try {
    const parsed = errorResponseSchema.safeParse(await readJson(response));
    if (parsed.success) envelope = parsed.data;
  } catch {
    // A non-JSON error body is not itself an error worth reporting; the status carries the news.
  }

  if (response.status === 401 && !ignoreUnauthorized) onUnauthorized?.();

  return new ApiError(
    'http',
    response.status,
    envelope?.error.code ?? fallbackCodeFor(response.status),
    envelope?.error.message ?? `The request failed with status ${String(response.status)}`,
    {
      ...(envelope?.error.details === undefined ? {} : { details: envelope.error.details }),
      requestId: envelope?.error.requestId,
    },
  );
}

/** Only reached when the body was not the envelope. The status is all there is to go on. */
function fallbackCodeFor(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'VALIDATION_FAILED';
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 429:
      return 'RATE_LIMITED';
    case 503:
      return 'SERVICE_UNAVAILABLE';
    default:
      return 'INTERNAL_ERROR';
  }
}
